import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '@/components/common/DataTable';
import { useDebounce } from '@/hooks';
import Loader from '@/components/common/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  Eye,
  Heart,
  MoreHorizontal,
  Receipt,
  ShareIcon,
  TrendingDown,
  XCircle,
} from 'lucide-react';
import { useGetAssetsInGroupQuery } from '@/store/api/allGroupApi';
import toast from 'react-hot-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateSharedAssetMutation } from '@/store/api/allGroupApi';

export type GroupAssetRow = {
  id: number;
  assetTitle: string;
  assetType: string;
  assetSubType: string;
  landArea: string;
  marketValue: number;
  location: string;
  status: string;
  currentOwnerName: string;
  tokenAmount: number | null;
  sharedAssetId: number;
};

type AssetStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

function GroupActions({ asset }: { asset: GroupAssetRow }) {
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState('');
  const [reasonError, setReasonError] = useState('');

  const [updateSharedAsset, { isLoading }] = useUpdateSharedAssetMutation();

  const handleSubmit = async () => {
    let isValid = true;

    setAmountError('');
    setReasonError('');

    if (actionType === 'APPROVED') {
      if (!amount || Number(amount) <= 0) {
        setAmountError('Please enter valid amount');
        isValid = false;
      }
    }

    if (actionType === 'REJECTED') {
      if (!reason.trim()) {
        setReasonError('Reject reason is required');
        isValid = false;
      }
    }

    if (!isValid) return;

    try {
      const payload: any = {
        sharedAssetId: asset.sharedAssetId,
        status: actionType,
      };

      if (actionType === 'APPROVED') {
        payload.tokenAmount = Number(amount);
      } else {
        payload.rejectNote = reason;
      }

      await updateSharedAsset(payload).unwrap();

      toast.success('Updated successfully');

      setOpen(false);
      setAmount('');
      setReason('');
    } catch (err: any) {
      toast.error(err?.data?.message || 'Something went wrong');
    }
  };
  return (
    <>
      {/* Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="w-8 h-8 p-0">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => {
              setActionType('APPROVED');
              setOpen(true);
            }}
            disabled={asset?.status !== 'PENDING'}
          >
            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
            Approve
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => {
              setActionType('REJECTED');
              setOpen(true);
            }}
            disabled={asset?.status !== 'PENDING'}
          >
            <XCircle className="w-4 h-4 mr-2 text-red-500" />
            Reject
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'APPROVED' ? 'Approve Asset' : 'Reject Asset'}
            </DialogTitle>
          </DialogHeader>

          {actionType === 'APPROVED' && (
            <>
              <Input
                type="number"
                placeholder="Enter Property Document Fees"
                value={amount}
                min={1}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setAmountError('');
                }}
              />

              {amountError && <p className="text-red-500 text-xs mt-1">{amountError}</p>}
            </>
          )}

          {actionType === 'REJECTED' && (
            <>
              <Textarea
                placeholder="Enter Reject Reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError('');
                }}
              />

              {reasonError && <p className="text-red-500 text-xs mt-1">{reasonError}</p>}
            </>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSubmit} disabled={isLoading}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
function StatusBadge({ status }: { status: string }) {
  if (status === 'APPROVED')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-400 rounded-full bg-green-500/10 border-green-500/20">
        <CheckCircle className="w-3 h-3" /> Approved
      </span>
    );

  if (status === 'REJECTED')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-400 rounded-full bg-red-500/10 border-red-500/20">
        <XCircle className="w-3 h-3" /> Rejected
      </span>
    );

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-yellow-500 rounded-full bg-yellow-500/10 border-yellow-500/20">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

export const mapGroupAssetToRow = (item: any): GroupAssetRow => {
  const asset = item?.sharedAsset?.asset;
  const meta = asset?.meta || [];

  const metaObj = Object.fromEntries(meta.map((m: any) => [m.metaKey, m.metaValue]));

  const location = [metaObj.city, metaObj.state, metaObj.country, metaObj.pincode]
    .filter(Boolean)
    .join(', ');

  return {
    id: item.id,
    assetTitle: asset?.assetTitle ?? '—',
    assetType: asset?.assetType ?? '—',
    assetSubType: metaObj.assetSubType ?? '—',
    landArea: metaObj.landArea ?? '—',
    marketValue: Number(metaObj.marketValue ?? 0),
    sharedAssetId: item.sharedAsset?.assetId,
    location,
    status: item?.sharedAsset?.status ?? '—',
    tokenAmount: item?.sharedAsset?.tokenAmount ?? null,
    currentOwnerName: asset?.currentOwnerName ?? '—',
  };
};

export function Properties() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [status, setStatus] = useState<AssetStatus | ''>('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { id } = useParams();

  const { data, isLoading, isError } = useGetAssetsInGroupQuery({
    groupId: Number(id),
    skip: pageIndex * pageSize,
    take: pageSize,
    search: debouncedSearch || undefined,
    status: status ? (status.toUpperCase() as AssetStatus) : undefined, // ✅ only send when selected
  });

  const totalCount = data?.count || 0;

  // const assets: InvestedAssetRow[] = data?.data?.map(mapInvestmentToRow) ?? [];

  // from new 6 update
  const assets: GroupAssetRow[] = data?.data?.map(mapGroupAssetToRow) ?? [];

  const columns: ColumnDef<GroupAssetRow>[] = [
    {
      header: 'S.No',
      cell: ({ row }) => <span>{pageIndex * pageSize + row.index + 1}</span>,
    },
    {
      header: 'Asset Title',
      accessorKey: 'assetTitle',
    },
    {
      header: 'Owner Name',
      accessorKey: 'currentOwnerName',
    },

    {
      header: 'Location',
      accessorKey: 'location',
    },

    {
      header: 'Market Value',
      cell: ({ row }) => `₹${row.original.marketValue.toLocaleString()}`,
    },
    {
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },

    {
      header: 'Share',
      cell: ({ row }) => (
        <button disabled={row.original.status !== 'APPROVED'}>
          <ShareIcon
            onClick={() => {
              navigate(`/group/share/${row.original.sharedAssetId}`);
            }}
            className={`cursor-pointer ${row.original.status !== 'APPROVED' ? 'text-gray-600' : 'text-white'}`}
          />
        </button>
      ),
    },

    {
      header: 'Intrested',
      cell: ({ row }) => (
        <Heart
          onClick={() => {
            navigate(`/group/intrested-member/${id}/${row.original.sharedAssetId}`);
          }}
          className="cursor-pointer"
        />
      ),
    },
    {
      header: 'Payments',
      cell: ({ row }) => (
        <Receipt
          onClick={() => {
            navigate(`/group/payments/${id}/${row.original.sharedAssetId}`);
          }}
          className="cursor-pointer"
        />
      ),
    },
    {
      header: 'Immediate Sale',
      cell: ({ row }) => (
        <TrendingDown
          onClick={() => navigate(`/group/immediate-sale/${id}/${row.original.sharedAssetId}`)}
          className="cursor-pointer"
        />
      ),
    },

    {
      header: 'Token Amount',
      cell: ({ row }) =>
        row.original.tokenAmount ? `₹${Number(row.original.tokenAmount).toLocaleString()}` : '—',
    },
    {
      header: 'Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/asset-list/assets/${row?.original?.sharedAssetId}`)}
          className="flex items-center justify-start w-full"
        >
          <Button
            variant="ghost"
            aria-label="eye "
            size="sm"
            className="w-8 h-8 p-0 hover:bg-blue-500/10 text-slate-400 hover:text-blue-400"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <GroupActions asset={row.original} />,
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <div className="p-6 text-red-500">Failed to load assets</div>;
  }

  return (
    <div className="space-y-8">
      <DataTable
        data={assets}
        columns={columns}
        pageSize={pageSize}
        pageIndex={pageIndex}
        searchPlaceholder="Search by property name"
        // pageCount={Math.ceil(totalCount / pageSize)}
        pageCount={Math.ceil(totalCount / pageSize)} // new 6 update
        manualPagination
        // enableFilters={false}
        search={search}
        enableStatusFilter={true}
        statusFilter={status ?? 'ALL'}
        onStatusFilterChange={(val) => {
          setStatus(val === 'ALL' ? '' : (val as AssetStatus));
          setPageIndex(0);
        }}
        onSearchChange={handleSearchChange}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
      />
    </div>
  );
}
